/**
 * useTheme — foolproof light/dark/auto mode hook (iter369 spec).
 *
 *   Modes:
 *     "auto"  → follow OS `prefers-color-scheme`   (default)
 *     "light" → force light                        (member override)
 *     "dark"  → force dark                         (member override)
 *
 *   1. First load → reads ``localStorage["hp-theme"]`` if set.
 *   2. Otherwise → defaults to "auto".
 *   3. On <html>:
 *        auto  → removes `data-hp-mode` + `data-theme`, syncs `.dark`
 *                class to OS preference so Tailwind `dark:` utilities
 *                still respond.
 *        light → sets `data-hp-mode="light"` + `data-theme="light"` +
 *                removes `.dark` class.
 *        dark  → sets `data-hp-mode="dark"` + `data-theme="dark"` +
 *                adds `.dark` class.
 *   4. Updates persist instantly across tabs (storage event) and
 *      react in real-time to OS-preference changes while "auto".
 *
 *   Backwards-compat: `theme` still exposes "dark" or "light" (auto is
 *   resolved to whichever the OS currently reports) so existing UI
 *   toggles keep working without a rewrite.
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "hp-theme";
const VALID = new Set(["auto", "light", "dark"]);

function readStored() {
  if (typeof window === "undefined") return "auto";
  const v = localStorage.getItem(KEY);
  return VALID.has(v) ? v : "auto";
}

function osPrefersDark() {
  if (typeof window === "undefined") return false;
  return !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

function apply(mode) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  if (mode === "auto") {
    html.removeAttribute("data-hp-mode");
    html.removeAttribute("data-theme");
    html.classList.toggle("dark", osPrefersDark());
  } else {
    html.setAttribute("data-hp-mode", mode);
    html.setAttribute("data-theme", mode);
    html.classList.toggle("dark", mode === "dark");
  }
}

export function useTheme() {
  const [mode, setMode] = useState(readStored);

  useEffect(() => {
    apply(mode);
    window.dispatchEvent(new CustomEvent("hp-theme-change",{detail:mode}));
    try { localStorage.setItem(KEY, mode); } catch {}
  }, [mode]);

  // Watch OS preference while in auto mode
  useEffect(() => {
    if (mode !== "auto") return;
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = () => apply("auto");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [mode]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY && VALID.has(e.newValue)) setMode(e.newValue);
    };
    const onLocal = (e) => { if (VALID.has(e.detail)) setMode(e.detail); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("hp-theme-change", onLocal);
    return () => { window.removeEventListener("storage", onStorage); window.removeEventListener("hp-theme-change", onLocal); };
  }, []);

  const resolved = mode === "auto"
    ? (osPrefersDark() ? "dark" : "light")
    : mode;

  const toggle = useCallback(() => {
    // Cycle light → dark → auto → light for members who want three-way
    setMode((m) => (m === "light" ? "dark" : m === "dark" ? "auto" : "light"));
  }, []);

  const setLight = useCallback(() => setMode("light"), []);
  const setDark  = useCallback(() => setMode("dark"), []);
  const setAuto  = useCallback(() => setMode("auto"), []);

  return {
    mode,           // "auto" | "light" | "dark"
    theme: resolved, // resolved "light" | "dark" (backwards-compat)
    isDark: resolved === "dark",
    isAuto: mode === "auto",
    toggle, setLight, setDark, setAuto,
  };
}
